import axios from "axios"
import { Identifier } from "ra-core"

import { LanguagePair , AllowedRole, Role, StageType } from "./common"

export type ProfileStatus = 'ORGANIZATION_ADMIN' | 'ORGANIZATION_MEMBER' | 'GATED_COMMUNITY_ADMIN' | 'GATED_COMMUNITY_MEMBER';

export interface GatedCommunitySearchRecord {
  id: string
  name: string
}

export interface GatedCommunityMemberSmallRecord {
  id: Identifier,
  userName: string,
  profile: ProfileStatus,
  roles: Role[],
  allowedRoles: AllowedRole[],
  languagePairs: LanguagePair[],
  allowedLanguagePairs: LanguagePair[],
  isNdaSigned: boolean,
  outOfOffice: boolean,
  isActive: boolean,
  isAccountConfirmed: boolean,
  score: number,
  latenessStats?: LatenessStats
}

export interface LatenessStats{
  latest: number,
  total: number
}

export interface GatedCommunityRecord {
  id: string,
  name: string,
  membersCount: number,
  activeMembersCount: number,
  recyclableTranslationLevel: string
  anonymizationEnabled: boolean,
  aiEnhancerEnabled: boolean,
  translationEngines?: NmtEngine[],
  jobsCount: number,
  latestJobsCount: number
}

export interface SimpleGatedCommunityRecord {
  id: string,
  name: string,
  recyclableTranslationLevel: string,
  anonymizationEnabled: boolean,
  aiEnhancerEnabled: boolean,
  translationEngines?: NmtEngine[]
}

export interface CandidateSearchResult {
  id: string,
  login: string,
  roles: Role[],
  communities: string[]
}

export async function getSearchGatedCommunity (search: string) {
  const response = await axios.get<GatedCommunitySearchRecord[]>(`/api/admin/gatedCommunities/search`, {
    params: { name: search }
  })
  return response.data
}

export const myCommunities = () => axios.get<{ gatedCommunityId:string, gatedCommunityName:string }[]>(`/api/workflow/groups_management/communities/my?isMember=true`)

export async function getGatedCommunityById (id: string) {
  const response = await axios.get<GatedCommunityRecord>(`/api/admin/gatedCommunities/${id}`)
  return response.data
}

export async function updateGatedCommunityMemberRoles (gcId: string, userId: string, roles: AllowedRole[]) {
  await axios.post(`/api/admin/gatedCommunities/${gcId}/members/${userId}/allowedRoles`, roles)
}

export async function updateGatedCommunityMemberLanguagePairs (gcId: string, userId: string, languagePairs: LanguagePair[]) {
  await axios.post(`/api/admin/gatedCommunities/${gcId}/members/${userId}/allowedLanguagePairs`, languagePairs)
}

export async function deleterGatedCommunityMember (gcId: string, userId: string) {
  await axios.delete(`/api/admin/gatedCommunities/${gcId}/members/${userId}`)
}

export async function searchCandidatesToGatedCommunity (gcId: string, name: string) {
  const response = await axios.get<CandidateSearchResult[]>(`/api/admin/gatedCommunities/${gcId}/candidates/search`, {
    params: { name }
  })
  return response.data
}

export async function addUserToGatedCommunity (gcId: string, userId: string) {
  await axios.put(`/api/admin/gatedCommunities/${gcId}/members/${userId}`)
}

export async function setUserProfileInGatedCommunity (gcId: string, userId: string, profile: ProfileStatus) {
  await axios.post(`/api/admin/gatedCommunities/${gcId}/members/${userId}/profile`, { profile })
}

export interface MaxExecutorAssignments {
  communityId: string
  stageType: StageType
  maxAmount: number
}

export interface UpdateMaxExecutorAssignments {
  stageType: StageType
  maxAmount: number
}

export interface MaxExecutorAssignmentsPerLanguagePair {
  limitId: string
  communityId: string
  stageType: StageType
  sourceLanguage: string
  targetLanguage: string
  maxAmount: number
}

export interface UpdateMaxExecutorAssignmentsPerLanguagePairRequest {
  stageType: StageType
  sourceLanguage: string
  targetLanguage: string
  maxAmount: number
}

export async function getLimitsOfMaxExecutorAssignments (communityId: string) {
  const response = await axios.get<MaxExecutorAssignments[]>(`/api/workflow/groups_management/v2/communities/${communityId}/limits/max-executor-assignments`)
  return response.data
}

export async function setLimitOfMaxExecutorAssignments (communityId: string, maxExecutorAssignments: UpdateMaxExecutorAssignments) {
  await axios.put<void>(`/api/workflow/groups_management/v2/communities/${communityId}/limits/max-executor-assignments`, maxExecutorAssignments)
}

export async function getLimitsOfMaxExecutorAssignmentsPerLanguagePair (communityId: string) {
  const response = await axios.get<MaxExecutorAssignmentsPerLanguagePair[]>(`/api/workflow/groups_management/v2/communities/${communityId}/limits/max-executor-assignments-per-language-pair`)
  return response.data
}

export async function createLimitOfMaxExecutorAssignmentsPerLanguagePair (communityId: string, maxExecutorAssignments: UpdateMaxExecutorAssignmentsPerLanguagePairRequest) {
  await axios.post<void>(`/api/workflow/groups_management/v2/communities/${communityId}/limits/max-executor-assignments-per-language-pair`, maxExecutorAssignments)
}

export async function updateLimitOfMaxExecutorAssignmentsPerLanguagePair (communityId: string, limitId: string, maxExecutorAssignments: UpdateMaxExecutorAssignmentsPerLanguagePairRequest) {
  await axios.put<void>(`/api/workflow/groups_management/v2/communities/${communityId}/limits/max-executor-assignments-per-language-pair/${limitId}`, maxExecutorAssignments)
}

export async function deleteLimitOfMaxExecutorAssignmentsPerLanguagePair (communityId: string, limitId: string) {
  await axios.delete<void>(`/api/workflow/groups_management/v2/communities/${communityId}/limits/max-executor-assignments-per-language-pair/${limitId}`)
}

export interface DocumentCount {
  community_id: string
  total: number
}

export async function getDocumentCounts (communityIds: string[]) {
  // FastAPI expects repeated query params: communityId=uuid1&communityId=uuid2
  const params = new URLSearchParams()
  communityIds.forEach(id => params.append('communityId', id))
  
  const response = await axios.get<DocumentCount[]>(`/km/api/km/ingest/documents/counts?${params.toString()}`)
  return response.data
}

export interface DocumentStatistics {
  total: number
  added_last_30_days: number
  pending: number
  approved_last_30_days: number
  deleted_total: number
  deleted_last_30_days: number
  errors: number
}

export async function getDocumentStatistics (communityId: string) {
  const response = await axios.get<DocumentStatistics>(`/km/api/km/ingest/documents/statistics`, {
    params: { communityId }
  })
  return response.data
}

export interface NmtEngine {
  id: string
  descriptiveName: string
}

export async function getEnabledNmtEngines () {
  const response = await axios.get<NmtEngine[]>(`/portal/api/engineAvailability/enabled`)
  return response.data
}